import { useParams, useSearchParams, Link } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { videos, categories } from "@/mocks/videos";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") || "";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug, searchQuery]);

  const category = useMemo(() => {
    return categories.find((c) => c.slug === slug);
  }, [slug]);

  const filteredVideos = useMemo(() => {
    let result = videos;
    if (slug && category) {
      result = result.filter((v) => v.category === slug);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          v.description.toLowerCase().includes(q)
      );
    }
    return result;
  }, [slug, category, searchQuery]);

  const pageTitle = searchQuery
    ? `搜索: ${searchQuery}`
    : category
    ? `${category.name} - ${category.description}`
    : "全部分类";

  return (
    <>
      <SEO
        title={pageTitle}
        description={
          category
            ? `久久视频${category.name}频道：${category.description}。免费在线观看最新最全${category.name}内容，每日更新。`
            : "久久视频全部分类内容，综艺、动漫、短视频、搞笑创意视频免费在线观看。"
        }
        keywords={
          category
            ? `久久视频,${category.name},${category.description},免费在线观看`
            : "久久视频,综艺,动漫,短视频,搞笑创意视频"
        }
        ogType="website"
      />
      <Navbar />

      <main className="pt-20">
        <div className="w-full bg-background-50 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: searchQuery ? "搜索" : category?.name || "全部分类" },
              ]}
            />

            <div className="py-6">
              {searchQuery ? (
                <div className="mb-6">
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
                    搜索: "{searchQuery}"
                  </h1>
                  <p className="text-foreground-600 text-sm mt-2">
                    找到 {filteredVideos.length} 个相关视频
                  </p>
                </div>
              ) : (
                <div className="mb-6">
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
                    {category?.name || "全部分类"}
                  </h1>
                  {category && (
                    <p className="text-foreground-600 text-sm mt-2">
                      {category.description}
                    </p>
                  )}
                </div>
              )}

              <div className="flex flex-col lg:flex-row gap-8">
                <aside className="lg:w-56 flex-shrink-0">
                  <h4 className="font-heading text-base text-foreground-900 mb-4">
                    视频分类
                  </h4>
                  <nav className="space-y-1">
                    <Link
                      to="/category/gaoxiao"
                      className={`block px-4 py-2.5 rounded-lg text-sm cursor-pointer transition-colors ${
                        slug === "gaoxiao"
                          ? "bg-primary-100 text-primary-600 font-medium"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                        <i className="ri-emotion-laugh-line"></i>
                      </span>
                      搞笑创意
                    </Link>
                    <Link
                      to="/category/zongyi"
                      className={`block px-4 py-2.5 rounded-lg text-sm cursor-pointer transition-colors ${
                        slug === "zongyi"
                          ? "bg-primary-100 text-primary-600 font-medium"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                        <i className="ri-tv-line"></i>
                      </span>
                      综艺
                    </Link>
                    <Link
                      to="/category/dongman"
                      className={`block px-4 py-2.5 rounded-lg text-sm cursor-pointer transition-colors ${
                        slug === "dongman"
                          ? "bg-primary-100 text-primary-600 font-medium"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                        <i className="ri-movie-line"></i>
                      </span>
                      动漫
                    </Link>
                    <Link
                      to="/category/duanshipin"
                      className={`block px-4 py-2.5 rounded-lg text-sm cursor-pointer transition-colors ${
                        slug === "duanshipin"
                          ? "bg-primary-100 text-primary-600 font-medium"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                        <i className="ri-vidicon-line"></i>
                      </span>
                      短视频
                    </Link>
                    {categories.slice(4).map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.slug}`}
                        className={`block px-4 py-2.5 rounded-lg text-sm cursor-pointer transition-colors ${
                          slug === cat.slug
                            ? "bg-primary-100 text-primary-600 font-medium"
                            : "text-foreground-700 hover:bg-background-100"
                        }`}
                      >
                        <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                          <i className={cat.icon}></i>
                        </span>
                        {cat.name}
                      </Link>
                    ))}
                  </nav>
                </aside>

                <div className="flex-1 min-w-0">
                  {filteredVideos.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                      {filteredVideos.map((video) => (
                        <VideoCard
                          key={video.id}
                          id={video.id}
                          title={video.title}
                          thumbnail={video.thumbnail}
                          duration={video.duration}
                          views={video.views}
                          date={video.date}
                          category={video.category}
                          categoryName={
                            categories.find((c) => c.slug === video.category)
                              ?.name || video.category
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-20">
                      <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                        <i className="ri-search-line text-2xl"></i>
                      </div>
                      <p className="text-foreground-600 text-sm">暂无相关视频内容</p>
                      <Link
                        to="/"
                        className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 cursor-pointer transition-colors"
                      >
                        返回首页
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}